<template>
  <section class="max-w-4xl space-y-4">
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Manage Access</h1>
        <p class="mt-1 text-sm text-gray-500">
          Grant a vendor's data to every member of an Auth0 organization. Membership itself is managed in Auth0.
        </p>
      </div>
      <button class="btn-primary" type="button" @click="openCreateDialog()">Add access</button>
    </header>

    <v-alert v-if="deleteError" type="error" :text="deleteError" closable @click:close="deleteError = null" />

    <v-alert v-if="vendorStore.error" type="error" :text="vendorStore.error" />

    <v-alert
      v-if="store.organizationsError"
      type="warning"
      :text="`Couldn't load organizations from Auth0 (${store.organizationsError}). Names are shown as raw IDs and new mappings must be entered by ID.`"
    />

    <v-alert v-if="store.error" type="error" :text="store.error" />
    <v-progress-linear v-else-if="store.loading || vendorStore.loading" indeterminate color="primary" />

    <div v-else>
      <p v-if="groups.length === 0" class="m-0 text-sm text-gray-500">
        No vendors imported yet — import a vendor on the Vendors page first.
      </p>
      <div v-else class="space-y-4">
        <div v-for="group in groups" :key="group.vendorId" class="rounded-xl border border-gray-200 bg-white p-4">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-baseline gap-2">
              <h2 class="m-0 text-sm font-semibold text-gray-900">{{ group.label }}</h2>
              <span class="text-xs text-gray-500">
                {{ group.mappings.length }} organization{{ group.mappings.length === 1 ? '' : 's' }}
              </span>
            </div>
            <button
              v-if="!group.archived"
              class="btn-secondary px-3 py-1 text-xs"
              type="button"
              @click="openCreateDialog(group.vendorId)"
            >
              Add
            </button>
          </div>

          <p v-if="group.mappings.length === 0" class="mt-3 text-sm text-gray-500">No organizations yet.</p>
          <table v-else class="mt-3 w-full border-collapse text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th class="py-2 pr-3">Organization</th>
                <th class="whitespace-nowrap py-2 pr-3">Granted by</th>
                <th class="whitespace-nowrap py-2 pr-3">Granted on</th>
                <th class="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in group.mappings" :key="mapping.id" class="border-b border-gray-100">
                <td class="py-2 pr-3 text-gray-900">
                  <span class="flex items-center gap-1">
                    <span class="flex flex-col">
                      <span>{{ organizationLabel(mapping) }}</span>
                      <span v-if="organizationLabel(mapping) !== mapping.auth0OrgId" class="text-xs text-gray-500">
                        {{ mapping.auth0OrgId }}
                      </span>
                    </span>
                    <button
                      type="button"
                      class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-700"
                      :aria-label="`Copy organization ID ${mapping.auth0OrgId}`"
                      @click="copyOrgId(mapping)"
                    >
                      <svg v-if="copiedId === mapping.id" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </span>
                </td>
                <td class="whitespace-nowrap py-2 pr-3 text-gray-500">{{ mapping.createdBy ?? '—' }}</td>
                <td class="whitespace-nowrap py-2 pr-3 text-gray-500">{{ formatDate(mapping.createdAt) }}</td>
                <td class="py-2 pr-3 text-right">
                  <button
                    type="button"
                    class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700"
                    :aria-label="`Revoke access for ${organizationLabel(mapping)}`"
                    @click="pendingDelete = mapping"
                  >
                    <v-icon icon="mdi-trash-can-outline" size="16" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="dialogOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        @click.self="closeDialog"
        @keydown.escape="closeDialog"
      >
        <form class="card w-full max-w-sm" @submit.prevent="submitDialog">
          <h3 class="m-0 text-base font-semibold text-gray-900">Add access</h3>

          <label class="mt-4 block text-xs font-medium text-gray-600" for="access-vendor">Vendor</label>
          <select
            id="access-vendor"
            v-model="form.vendorId"
            :disabled="vendorStore.loading"
            :aria-invalid="vendorFieldError ? 'true' : undefined"
            :aria-describedby="vendorFieldError ? 'access-vendor-error' : undefined"
            class="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="" disabled>Select a vendor</option>
            <option v-for="vendor in vendorStore.activeVendors" :key="vendor.id" :value="vendor.id">
              {{ vendorLabel(vendor.id) }}
            </option>
          </select>
          <p v-if="vendorFieldError" id="access-vendor-error" class="mt-1 text-sm text-red-700" role="alert">
            {{ vendorFieldError }}
          </p>

          <label class="mt-3 block text-xs font-medium text-gray-600" for="access-organization">
            {{ hasOrganizationOptions ? 'Organization' : 'Auth0 organization ID' }}
          </label>
          <select
            v-if="hasOrganizationOptions"
            id="access-organization"
            v-model="form.auth0OrgId"
            :aria-invalid="organizationFieldError ? 'true' : undefined"
            :aria-describedby="organizationFieldError ? 'access-organization-error' : undefined"
            class="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="" disabled>Select an organization</option>
            <option v-for="organization in availableOrganizations" :key="organization.id" :value="organization.id">
              {{ organization.displayName }} ({{ organization.id }})
            </option>
          </select>
          <input
            v-else
            id="access-organization"
            v-model="form.auth0OrgId"
            type="text"
            placeholder="org_…"
            :aria-invalid="organizationFieldError ? 'true' : undefined"
            :aria-describedby="organizationFieldError ? 'access-organization-error' : undefined"
            class="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
          <p v-if="organizationFieldError" id="access-organization-error" class="mt-1 text-sm text-red-700" role="alert">
            {{ organizationFieldError }}
          </p>

          <p v-if="dialogError" class="mt-3 text-sm text-red-700" role="alert">{{ dialogError }}</p>

          <div class="mt-5 flex justify-end gap-2">
            <button class="btn-secondary" type="button" @click="closeDialog">Cancel</button>
            <button class="btn-primary" type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Add access' }}</button>
          </div>
        </form>
      </div>
    </Teleport>

    <ConfirmDialog
      :open="pendingDelete !== null"
      title="Revoke access?"
      :message="deleteMessage"
      confirm-label="Revoke"
      @confirm="confirmDelete"
      @cancel="pendingDelete = null"
    />
  </section>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { useVendorOrganizationsStore } from '@/store/vendorOrganizationsStore';
  import { useVendorStore } from '@/store/vendorStore';
  import type { Auth0Organization, VendorOrganizationMapping } from '@/types/vendorOrganization';
  import { extractErrorMessage } from '@/utils/apiError';
import { isArchived, vendorOptionLabel } from '@/utils/vendorArchive';

  const COPY_FEEDBACK_MS = 2000;

  const store = useVendorOrganizationsStore();
  const vendorStore = useVendorStore();

  const dialogOpen = ref(false);
  const saving = ref(false);
  const dialogError = ref<string | null>(null);
  const form = reactive({ auth0OrgId: '', vendorId: '' });

  const vendorFieldError = ref<string | null>(null);
  const organizationFieldError = ref<string | null>(null);

  const pendingDelete = ref<VendorOrganizationMapping | null>(null);
  const deleteError = ref<string | null>(null);
  const deleteMessage = computed(() =>
    pendingDelete.value
      ? `Members of ${organizationLabel(pendingDelete.value)} will lose access to ${vendorLabel(pendingDelete.value.vendorId)}'s data.`
      : '',
  );

  const copiedId = ref<string | null>(null);
  let copyFeedbackTimeout: ReturnType<typeof setTimeout> | undefined;

  const hasOrganizationOptions = computed(() => store.organizations.length > 0);

  const availableOrganizations = computed<Auth0Organization[]>(() => {
    const mappedOrgIds = new Set(
      store.items.filter((mapping) => mapping.vendorId === form.vendorId).map((mapping) => mapping.auth0OrgId),
    );
    return store.organizations.filter((organization) => !mappedOrgIds.has(organization.id));
  });

  watch(
    () => form.vendorId,
    (value) => {
      if (value) vendorFieldError.value = null;
      // A previously-picked org can drop out of the select's options when the vendor
      // changes (it's already mapped to the new vendor) — the native <select> then shows
      // blank while form.auth0OrgId still holds the stale id, which would otherwise submit
      // silently. Only the select mode has a closed option set; leave typed-in ids alone.
      if (
        hasOrganizationOptions.value &&
        form.auth0OrgId &&
        !availableOrganizations.value.some((organization) => organization.id === form.auth0OrgId)
      ) {
        form.auth0OrgId = '';
      }
    },
  );

  watch(
    () => form.auth0OrgId,
    (value) => {
      if (value.trim()) organizationFieldError.value = null;
    },
  );

  async function copyOrgId(mapping: VendorOrganizationMapping): Promise<void> {
    try {
      await navigator.clipboard.writeText(mapping.auth0OrgId);
    } catch {
      return;
    }
    if (copyFeedbackTimeout) clearTimeout(copyFeedbackTimeout);
    copiedId.value = mapping.id;
    copyFeedbackTimeout = setTimeout(() => {
      copiedId.value = null;
    }, COPY_FEEDBACK_MS);
  }

  function vendorLabel(vendorId: string): string {
    const vendor = vendorStore.vendors.find((candidate) => candidate.id === vendorId);
    return vendor ? vendor.alias || vendor.name : vendorId;
  }

  function groupLabel(vendorId: string): string {
    const vendor = vendorStore.vendors.find((candidate) => candidate.id === vendorId);
    return vendor ? vendorOptionLabel(vendor) : vendorId;
  }

  function organizationLabel(mapping: VendorOrganizationMapping): string {
    return store.organizationLabel(mapping.auth0OrgId);
  }

  interface AccessGroup {
    vendorId: string;
    label: string;
    archived: boolean;
    mappings: VendorOrganizationMapping[];
  }

  const groups = computed<AccessGroup[]>(() => {
    const byVendor = new Map<string, VendorOrganizationMapping[]>();
    for (const mapping of store.items) {
      const bucket = byVendor.get(mapping.vendorId);
      if (bucket) {
        bucket.push(mapping);
      } else {
        byVendor.set(mapping.vendorId, [mapping]);
      }
    }

    const knownVendorIds = new Set(vendorStore.vendors.map((vendor) => vendor.id));

    const vendorGroups = vendorStore.vendors
      .map((vendor) => ({
        vendorId: vendor.id,
        label: groupLabel(vendor.id),
        archived: isArchived(vendor),
        mappings: [...(byVendor.get(vendor.id) ?? [])].sort((a, b) => a.auth0OrgId.localeCompare(b.auth0OrgId)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const orphanGroups = Array.from(byVendor.entries())
      .filter(([vendorId]) => !knownVendorIds.has(vendorId))
      .map(([vendorId, mappings]) => ({
        vendorId,
        label: groupLabel(vendorId),
        archived: false,
        mappings: [...mappings].sort((a, b) => a.auth0OrgId.localeCompare(b.auth0OrgId)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...vendorGroups, ...orphanGroups];
  });

  function openCreateDialog(vendorId?: string): void {
    form.auth0OrgId = '';
    form.vendorId = vendorId ?? '';
    dialogError.value = null;
    vendorFieldError.value = null;
    organizationFieldError.value = null;
    dialogOpen.value = true;
  }

  function closeDialog(): void {
    dialogOpen.value = false;
  }

  async function focusField(id: string): Promise<void> {
    await nextTick();
    document.getElementById(id)?.focus();
  }

  async function submitDialog(): Promise<void> {
    vendorFieldError.value = form.vendorId ? null : 'Choose a vendor.';
    organizationFieldError.value = form.auth0OrgId.trim()
      ? null
      : hasOrganizationOptions.value
        ? 'Choose an organization.'
        : 'Enter the organization ID.';

    if (vendorFieldError.value) {
      await focusField('access-vendor');
      return;
    }
    if (organizationFieldError.value) {
      await focusField('access-organization');
      return;
    }

    saving.value = true;
    dialogError.value = null;
    try {
      await store.create({ auth0OrgId: form.auth0OrgId.trim(), vendorId: form.vendorId });
      dialogOpen.value = false;
    } catch (submitError) {
      dialogError.value = extractErrorMessage(submitError);
    } finally {
      saving.value = false;
    }
  }

  async function confirmDelete(): Promise<void> {
    deleteError.value = null;
    const mapping = pendingDelete.value;
    pendingDelete.value = null;
    if (!mapping) return;
    try {
      await store.remove(mapping.id);
    } catch (removeError) {
      deleteError.value = extractErrorMessage(removeError);
    }
  }

  function formatDate(value: string): string {
    return value.slice(0, 'YYYY-MM-DD'.length);
  }

  onMounted(() => {
    void store.load();
    void vendorStore.fetchVendors();
  });
</script>
